import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, ArrowRight } from "lucide-react";

const STORAGE_KEY = "cleany_exit_intent_dismissed";

export default function ExitIntentPopup({ onCtaClick }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        // Don't fire if already dismissed/converted in this session
        if (sessionStorage.getItem(STORAGE_KEY) === "1") return;

        let fired = false;
        const handleMouseOut = (e) => {
            if (fired) return;
            // User moves cursor up out of the viewport
            if (e.clientY <= 0 && (e.relatedTarget == null || e.toElement == null)) {
                fired = true;
                setOpen(true);
            }
        };
        const handleScrollExit = () => {
            // Mobile fallback: trigger when user scrolls back up after going far down
            if (fired) return;
            // no-op for now; primary trigger is desktop mouseout
        };

        // Delay activation 8s so we don't annoy fast skimmers
        const t = setTimeout(() => {
            document.addEventListener("mouseout", handleMouseOut);
            window.addEventListener("scroll", handleScrollExit, { passive: true });
        }, 8000);

        return () => {
            clearTimeout(t);
            document.removeEventListener("mouseout", handleMouseOut);
            window.removeEventListener("scroll", handleScrollExit);
        };
    }, []);

    const close = () => {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setOpen(false);
    };

    const accept = () => {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setOpen(false);
        onCtaClick();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[120] flex items-center justify-center px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="exit-intent-popup"
                >
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={close} />
                    <motion.div
                        initial={{ scale: 0.95, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.95, y: 20, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
                    >
                        <button
                            onClick={close}
                            className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center"
                            aria-label="Luk"
                            data-testid="exit-intent-close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <div className="relative px-8 pt-10 pb-8">
                            <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-sky-100/70 blur-3xl" />
                            <div className="relative">
                                <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[11px] font-bold text-amber-800">
                                    <Sparkles className="h-3 w-3" />
                                    Vent — inden du går
                                </div>
                                <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                                    Vent — inden du går.
                                </h3>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                    Tilmeld dig ventelisten på 30 sekunder og få{" "}
                                    <strong>14 dages gratis prøveperiode</strong> når
                                    Cleany launcher 1. oktober 2026. Ingen kreditkort, ingen binding.
                                </p>
                                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                                    {[
                                        { v: "14 dg", l: "gratis" },
                                        { v: "0 kr", l: "kreditkort" },
                                        { v: "Fri", l: "opsigelse" },
                                    ].map((s) => (
                                        <div key={s.l} className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                                            <div className="font-display font-bold text-sky-700 text-lg leading-none">{s.v}</div>
                                            <div className="text-[10px] text-slate-500 mt-0.5">{s.l}</div>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    onClick={accept}
                                    data-testid="exit-intent-accept"
                                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold h-12 shadow-lg shadow-sky-600/30 transition-all active:scale-[0.98]"
                                >
                                    Start med 14 dages gratis prøveperiode
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={close}
                                    className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700 py-2"
                                >
                                    Nej tak, ikke lige nu
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
